import { useIntl } from "react-intl";
import { FilterChip } from "@/components/design/FilterChip";
import { MultiSelect } from "@/components/design/MultiSelect";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Select } from "@/components/design/Select";
import type { FieldOption } from "@/lib/field-util";
import {
  EMPTY_TALENT_FILTERS,
  EMPTY_TITLE_FILTERS,
  talentFiltersActive,
  titleFiltersActive,
  type FilterOptions,
  type KeywordMatch,
  type TobisTalentFilters,
  type TobisTalentSort,
  type TobisTitleFilters,
  type TobisTitleSort,
} from "@/lib/tobis";
import { TobisKeywordFilter } from "./TobisKeywordFilter";
import { TobisMinSlider, TobisRangeSlider, type HistogramBucket } from "./TobisHistogramSlider";
import { TobisSearch } from "./TobisPageShell";

// Filterleisten fuer Register und Personen. Dieselbe Semantik wie die erweiterte
// Suche (flaches Filterobjekt, Mehrfachauswahl, Bereiche), aber ohne
// GraphQL-Where-Struktur: der Stand wird als Query-Parameter geschickt und in
// SQL uebersetzt.
//
// Auswahllisten kommen aus dem vorhandenen Bestand (GET filter-options), damit
// die Filter nur zeigen, was auch Treffer liefern kann.

function asOptions(values: readonly string[]): FieldOption[] {
  return values.map((v) => ({ value: v, label: v }));
}

// Kuratierte Vorauswahl der global bekanntesten Filmpreise (Marvin,
// 2026-08-06), damit man sie nicht in ~250 Auszeichnungs-Familien suchen
// muss. Haeufigkeit im Bestand ist kein Prestige-Mass: AACTA Award/European
// Film Award haben mehr Gewinner-Zeilen als Golden Globe/BAFTA (siehe
// awardOptions in db.ts), waeren also keine brauchbare Sortierung fuer
// "am schnellsten wiederfinden". Reihenfolge hier ist die Anzeige-Reihenfolge
// der Gruppe, nicht die Haeufigkeit.
const WELL_KNOWN_AWARDS = [
  "Academy Award",
  "Golden Globe Award",
  "BAFTA Award",
  "Screen Actors Guild Award",
  "Palme d'Or",
  "Golden Lion",
  "Golden Bear",
];

// Wie asOptions, aber die bekanntesten Preise stehen unter einem eigenen
// Section-Kopf vorn (MultiSelect gruppiert bereits, siehe renderGrouped) - nur
// Preise, die im aktuellen Bestand ueberhaupt Treffer haben, damit die Gruppe
// nicht mit leeren Eintraegen anfaengt.
function groupedAwardOptions(values: readonly string[], sectionLabel: string): FieldOption[] {
  const present = new Set(values);
  const known = WELL_KNOWN_AWARDS.filter((v) => present.has(v));
  const knownSet = new Set(known);
  const rest = values.filter((v) => !knownSet.has(v));
  return [
    ...known.map((v) => ({ value: v, label: v, section: sectionLabel })),
    ...rest.map((v) => ({ value: v, label: v })),
  ];
}

// Zahlenfeld fuer Bereichsgrenzen. Leer bedeutet "kein Filter", deshalb Text und
// keine Zahl im State.
// Bleibt fuer die Personenliste (Alter, Rollen ab) - dort ist noch keine
// Verteilung angefragt, siehe TobisMinSlider/TobisRangeSlider fuer die Titelliste.
function RangeInput({
  value,
  onChange,
  label,
  placeholder,
  width = "w-16",
}: {
  value: string;
  onChange: (next: string) => void;
  label: string;
  placeholder?: string;
  width?: string;
}) {
  return (
    <input
      type="number"
      inputMode="numeric"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      placeholder={placeholder}
      className={`${width} rounded-md bg-page px-2 py-1 text-[13px] tabular-nums outline-none ring-1 ring-ink/10 focus-visible:ring-ink/30`}
    />
  );
}

// Untergrenze eines Buckets (`buckets[i].value`) plus Bucket-Breite, oder ein
// Rueckfall, solange FilterOptions noch nicht geladen ist (options ist bis
// dahin null - MultiSelect bekommt in der Zeit eine leere Liste, der Slider
// braucht dagegen ZWINGEND ein Intervall, auch ein grob geschaetztes.
function bucketDomain(buckets: HistogramBucket[] | undefined, bucketSize: number, fallback: [number, number]) {
  if (!buckets?.length) return { min: fallback[0], max: fallback[1] };
  const values = buckets.map((b) => b.value);
  return { min: Math.min(...values), max: Math.max(...values) + bucketSize };
}

// Stimmen/Besucherzahlen sind rechtsschief verteilt (siehe
// FilterDistributions.votes) - der Regler bewegt sich deshalb in
// Zehnerpotenzen (0 = "ab 1", 3 = "ab 1.000"), nicht in der Rohzahl, sonst
// laege die gesamte Masse in den ersten paar Pixeln.
function exponentFromValue(raw: string): number {
  if (raw === "") return 0;
  const n = Number(raw);
  return n <= 1 ? 0 : Math.round(Math.log10(n));
}
function valueFromExponent(exp: number): string {
  return exp <= 0 ? "" : String(10 ** exp);
}

// Label oben, Control darunter — dieselbe Huelle wie das SHARE-FILTER-Feld in
// ExtendedSearchPage.tsx (dort lokal "Field" genannt): fuer ein Formular mit
// mehreren gleichwertigen Filterfeldern nebeneinander, statt Label-links wie
// bei einem einzelnen Suchfeld.
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.15em] text-ink/45">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ResetButton({ onClick }: { onClick: () => void }) {
  const intl = useIntl();
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[12px] text-ink/45 underline decoration-ink/20 underline-offset-2 transition hover:text-ink"
    >
      {intl.formatMessage({ id: "tobis.filter.reset", defaultMessage: "Reset filters" })}
    </button>
  );
}

export function TobisTitleFilterBar({
  filters,
  onChange,
  options,
  sort,
  onSortChange,
  selectedKeywords,
  onKeywordsChange,
  keywordMatchAll,
  onKeywordMatchAllChange,
}: {
  filters: TobisTitleFilters;
  onChange: (next: TobisTitleFilters) => void;
  options: FilterOptions | null;
  sort: TobisTitleSort;
  onSortChange: (next: TobisTitleSort) => void;
  /**
   * Nur gesetzt in der normalen Titelliste (TobisTitlesPage). Im Labor bleibt
   * es weg: dort kommen Keywords aus dem Pitch-Text und werden schon oberhalb
   * dieser Leiste angezeigt, eine zweite Auswahlmoeglichkeit hier waere
   * verwirrend statt hilfreich.
   */
  selectedKeywords?: readonly KeywordMatch[];
  onKeywordsChange?: (next: KeywordMatch[]) => void;
  /** "Oder"/"Und" fuer selectedKeywords, siehe TobisKeywordFilter. Nur zusammen mit onKeywordsChange sinnvoll. */
  keywordMatchAll?: boolean;
  onKeywordMatchAllChange?: (next: boolean) => void;
}) {
  const intl = useIntl();
  const set = <K extends keyof TobisTitleFilters>(key: K, value: TobisTitleFilters[K]) =>
    onChange({ ...filters, [key]: value });

  // Rueckfaelle grob am Bestand orientiert (Stand 2026-08-06), solange
  // FilterOptions noch nicht geladen ist - siehe bucketDomain oben.
  const yearDomain = bucketDomain(options?.distributions?.year, 5, [1900, 2026]);
  const votesDomain = bucketDomain(options?.distributions?.votes, 1, [0, 6]);
  const admissionsDomain = bucketDomain(options?.distributions?.admissions, 1, [0, 7]);

  const media: Array<{ value: TobisTitleFilters["medium"]; labelId: string; fallback: string }> = [
    { value: "", labelId: "tobis.filter.all", fallback: "All" },
    { value: "movie", labelId: "tobis.filter.movies", fallback: "Films" },
    { value: "series", labelId: "tobis.filter.series", fallback: "Series" },
  ];

  // Wie beim Sort-Select der Personenliste: die gewaehlte Option ist
  // selbsterklaerend, deshalb kein Label daneben.
  const sortOptions: FieldOption[] = [
    {
      value: "release",
      label: intl.formatMessage({ id: "tobis.sort.newest", defaultMessage: "Newest first" }),
    },
    {
      value: "votes",
      label: intl.formatMessage({ id: "tobis.sort.relevance", defaultMessage: "Relevance" }),
    },
    {
      value: "updated",
      label: intl.formatMessage({ id: "tobis.sort.updated", defaultMessage: "Recently updated" }),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <TobisSearch
          value={filters.search}
          onChange={(v) => set("search", v)}
          label={intl.formatMessage({
            id: "tobis.titles.searchLabel",
            defaultMessage: "Search titles",
          })}
          placeholder={intl.formatMessage({
            id: "tobis.titles.searchPlaceholder",
            defaultMessage: "Search by name",
          })}
        />
        <div className="flex gap-1.5">
          {media.map((m) => (
            <FilterChip
              key={m.value || "all"}
              active={filters.medium === m.value}
              onClick={() => set("medium", m.value)}
            >
              {intl.formatMessage({ id: m.labelId, defaultMessage: m.fallback })}
            </FilterChip>
          ))}
        </div>
        {onKeywordsChange ? (
          <TobisKeywordFilter
            selected={selectedKeywords ?? []}
            onChange={onKeywordsChange}
            matchAll={keywordMatchAll ?? false}
            onMatchAllChange={onKeywordMatchAllChange}
          />
        ) : null}
        {titleFiltersActive(filters) ? (
          <ResetButton onClick={() => onChange(EMPTY_TITLE_FILTERS)} />
        ) : null}
        <div className="ml-auto">
          <Select
            value={sort}
            onChange={(v) => onSortChange(v as TobisTitleSort)}
            options={sortOptions}
            hideEmpty
            className="w-40 !rounded-full !bg-page !py-1.5 !text-[12px]"
          />
        </div>
      </div>

      {/* Alle Felder offen im Grid, derselbe Zuschnitt wie die Personenliste
          darunter (SHARE-FILTER aus ExtendedSearchPage: Label oben, Control
          darunter). Vorher lagen sie hinter "+ Filter hinzufuegen" - bei sieben
          Feldern, die alle regelmaessig gebraucht werden, kostet das nur Klicks
          und verbirgt, welche Filter es ueberhaupt gibt.
          grid-cols-N (nicht [repeat(N,max-content)]) ist bewusst: Tailwinds
          numerische Spalten sind minmax(0,1fr), die Reihe kann also nie breiter
          werden als der Container. Mit max-content sizt jede Spalte auf den
          breitesten Inhalt UEBER ALLE Zeilen (hier: die w-64-Auszeichnung),
          das riss die Leiste im schmaleren Pitch-Schritt seitlich auf. Jedes
          Feld deshalb w-full statt einer eigenen festen Breite - die Zelle
          bestimmt die Breite, nicht mehr das Feld. */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 @sm:grid-cols-4">
        <Field label={intl.formatMessage({ id: "tobis.filter.genres", defaultMessage: "Genres" })}>
          <MultiSelect
            options={asOptions(options?.genres ?? [])}
            selected={filters.genres}
            onChange={(next) => set("genres", next)}
            placeholder={intl.formatMessage({ id: "tobis.filter.genres", defaultMessage: "Genres" })}
            searchable
            className="w-full"
          />
        </Field>

        <Field
          label={intl.formatMessage({ id: "tobis.filter.languages", defaultMessage: "Languages" })}
        >
          <MultiSelect
            options={asOptions(options?.languages ?? [])}
            selected={filters.languages}
            onChange={(next) => set("languages", next)}
            placeholder={intl.formatMessage({
              id: "tobis.filter.languages",
              defaultMessage: "Languages",
            })}
            searchable
            className="w-full"
          />
        </Field>

        {/* Quellen tragen einen eigenen Anzeigenamen aus ingest.source, deshalb
            keine asOptions-Umwandlung wie bei Genres/Sprachen. */}
        <Field label={intl.formatMessage({ id: "tobis.filter.sources", defaultMessage: "Sources" })}>
          <MultiSelect
            options={options?.sources ?? []}
            selected={filters.sources}
            onChange={(next) => set("sources", next)}
            placeholder={intl.formatMessage({
              id: "tobis.filter.sources",
              defaultMessage: "Sources",
            })}
            searchable
            className="w-full"
          />
        </Field>

        {/* Firmennamen kommen 1:1 aus TMDB, keine asOptions-Umwandlung wie bei
            Genres: der Wert IST das Label. Nur Firmen mit mehreren Titeln
            (Rauschfilter im BFF, siehe companyOptions), sonst waeren es
            26.000+ Eintraege statt einer durchsuchbaren Handvoll Tausend. */}
        <Field
          label={intl.formatMessage({ id: "tobis.filter.companies", defaultMessage: "Production company" })}
        >
          <MultiSelect
            options={asOptions(options?.companies ?? [])}
            selected={filters.companies}
            onChange={(next) => set("companies", next)}
            placeholder={intl.formatMessage({
              id: "tobis.filter.companies",
              defaultMessage: "Production company",
            })}
            searchable
            className="w-full"
          />
        </Field>

        {/* Nur bei source='dfff' gesetzt (model.dfff_projekt.verleih) - nur 130
            distincte Werte, keine Rauschgrenze wie bei Produktionsfirma noetig. */}
        <Field
          label={intl.formatMessage({ id: "tobis.filter.distributors", defaultMessage: "Distributor" })}
        >
          <MultiSelect
            options={asOptions(options?.distributors ?? [])}
            selected={filters.distributors}
            onChange={(next) => set("distributors", next)}
            placeholder={intl.formatMessage({
              id: "tobis.filter.distributors",
              defaultMessage: "Distributor",
            })}
            searchable
            className="w-full"
          />
        </Field>

        <Field label={intl.formatMessage({ id: "tobis.filter.year", defaultMessage: "Year" })}>
          <div className="w-full">
            <TobisRangeSlider
              buckets={options?.distributions?.year ?? []}
              min={yearDomain.min}
              max={yearDomain.max}
              step={5}
              from={filters.yearFrom === "" ? yearDomain.min : Number(filters.yearFrom)}
              to={filters.yearTo === "" ? yearDomain.max : Number(filters.yearTo)}
              onChange={(from, to) =>
                onChange({
                  ...filters,
                  yearFrom: from <= yearDomain.min ? "" : String(from),
                  yearTo: to >= yearDomain.max ? "" : String(to),
                })
              }
              formatValue={(v) => String(v)}
              ariaLabelFrom={intl.formatMessage({ id: "tobis.filter.yearFrom", defaultMessage: "Year from" })}
              ariaLabelTo={intl.formatMessage({ id: "tobis.filter.yearTo", defaultMessage: "Year to" })}
            />
          </div>
        </Field>

        <Field
          label={intl.formatMessage({ id: "tobis.filter.ratingFrom", defaultMessage: "Rating from" })}
        >
          <div className="w-full">
            <TobisMinSlider
              buckets={options?.distributions?.rating ?? []}
              min={0}
              max={10}
              step={0.5}
              value={filters.ratingMin === "" ? 0 : Number(filters.ratingMin)}
              onChange={(v) => set("ratingMin", v <= 0 ? "" : String(v))}
              formatValue={(v) => v.toFixed(1)}
              ariaLabel={intl.formatMessage({ id: "tobis.filter.ratingFrom", defaultMessage: "Rating from" })}
            />
          </div>
        </Field>

        <Field
          label={intl.formatMessage({ id: "tobis.filter.votesFrom", defaultMessage: "Votes from" })}
        >
          <div className="w-full">
            <TobisMinSlider
              buckets={options?.distributions?.votes ?? []}
              min={0}
              max={votesDomain.max}
              step={1}
              value={exponentFromValue(filters.votesMin)}
              onChange={(exp) => set("votesMin", valueFromExponent(exp))}
              formatValue={(exp) => (exp <= 0 ? "1" : intl.formatNumber(10 ** exp))}
              ariaLabel={intl.formatMessage({ id: "tobis.filter.votesFrom", defaultMessage: "Votes from" })}
            />
          </div>
        </Field>

        <Field
          label={intl.formatMessage({
            id: "tobis.filter.admissionsFrom",
            defaultMessage: "DE admissions from",
          })}
        >
          <div className="w-full">
            <TobisMinSlider
              buckets={options?.distributions?.admissions ?? []}
              min={0}
              max={admissionsDomain.max}
              step={1}
              value={exponentFromValue(filters.admissionsMin)}
              onChange={(exp) => set("admissionsMin", valueFromExponent(exp))}
              formatValue={(exp) => (exp <= 0 ? "1" : intl.formatNumber(10 ** exp))}
              ariaLabel={intl.formatMessage({
                id: "tobis.filter.admissionsFrom",
                defaultMessage: "DE admissions from",
              })}
            />
          </div>
        </Field>

        <Field
          label={intl.formatMessage({ id: "tobis.filter.runtimeFrom", defaultMessage: "Runtime from" })}
        >
          <div className="w-full">
            <TobisMinSlider
              buckets={options?.distributions?.runtime ?? []}
              min={0}
              max={240}
              step={15}
              value={filters.runtimeMin === "" ? 0 : Number(filters.runtimeMin)}
              onChange={(v) => set("runtimeMin", v <= 0 ? "" : String(v))}
              formatValue={(v) =>
                intl.formatMessage(
                  { id: "tobis.filter.runtimeValue", defaultMessage: "{minutes} min" },
                  { minutes: v >= 240 ? "240+" : v },
                )
              }
              ariaLabel={intl.formatMessage({ id: "tobis.filter.runtimeFrom", defaultMessage: "Runtime from" })}
            />
          </div>
        </Field>

        {/* t.country traegt mehrere Laender je Zeile (IMDb-Freitext), im BFF
            per string_to_array gesplittet - die Optionsliste (titleCountries)
            zeigt entsprechend einzelne Laender, keine Rohwerte. */}
        <Field
          label={intl.formatMessage({ id: "tobis.filter.productionCountry", defaultMessage: "Production country" })}
        >
          <MultiSelect
            options={asOptions(options?.titleCountries ?? [])}
            selected={filters.countries}
            onChange={(next) => set("countries", next)}
            placeholder={intl.formatMessage({
              id: "tobis.filter.productionCountry",
              defaultMessage: "Production country",
            })}
            searchable
            className="w-full"
          />
        </Field>

        {/* Wirkt faktisch nur auf Serien (network ist bei Filmen fast immer
            leer) - dieselbe steuerbar-statt-versteckt-Haltung wie beim
            Quelle-Filter bei Personen. */}
        <Field label={intl.formatMessage({ id: "tobis.filter.networks", defaultMessage: "Network" })}>
          <MultiSelect
            options={asOptions(options?.networks ?? [])}
            selected={filters.networks}
            onChange={(next) => set("networks", next)}
            placeholder={intl.formatMessage({ id: "tobis.filter.networks", defaultMessage: "Network" })}
            searchable
            className="w-full"
          />
        </Field>

        {/* Wirkt faktisch nur auf Serien. statusOptions filtert im BFF eine
            Handvoll MPAA-Freigaben raus, die als Datenaltlast im selben Feld
            stehen (siehe filters.ts) - was hier ankommt, sind echte Status. */}
        <Field label={intl.formatMessage({ id: "tobis.filter.status", defaultMessage: "Status" })}>
          <MultiSelect
            options={asOptions(options?.statuses ?? [])}
            selected={filters.statuses}
            onChange={(next) => set("statuses", next)}
            placeholder={intl.formatMessage({ id: "tobis.filter.status", defaultMessage: "Status" })}
            className="w-full"
          />
        </Field>

        {/* Matcht nur GEWONNENE Auszeichnungen (model.award.won), nominiert
            allein reicht nicht - siehe titleWhere. Quelle: Wikidata, nicht das
            OMDb-Freitextfeld awardsText/awardWins (kein Einzelposten). Werte
            sind Auszeichnungs-Familien (model.award_family, siehe V0056),
            nicht die einzelnen Kategorien - "Academy Award" statt 25 "Academy
            Award for X"-Zeilen. Lange Familiennamen ("Dallas-Fort Worth Film
            Critics Association Award") laufen im Dropdown ueber MultiSelects
            eigenes truncate, keine eigene Breite mehr noetig - w-full wie
            jedes andere Feld hier, siehe Grid-Kommentar oben. */}
        <Field
          label={intl.formatMessage({ id: "tobis.filter.awardsWon", defaultMessage: "Won award" })}
        >
          <MultiSelect
            options={groupedAwardOptions(
              options?.awards ?? [],
              intl.formatMessage({ id: "tobis.filter.awardsWonWellKnown", defaultMessage: "Most known" }),
            )}
            selected={filters.awardsWon}
            onChange={(next) => set("awardsWon", next)}
            placeholder={intl.formatMessage({ id: "tobis.filter.awardsWon", defaultMessage: "Won award" })}
            searchable
            className="w-full"
          />
        </Field>

        {/* Reines Vorhandensein (model.title_script), kein Wert - siehe titleWhere. */}
        <Field label={intl.formatMessage({ id: "tobis.filter.hasScript", defaultMessage: "Screenplay" })}>
          <label className="flex h-9 cursor-pointer items-center gap-2 text-[13px] text-ink/80">
            <input
              type="checkbox"
              checked={filters.hasScript}
              onChange={(e) => set("hasScript", e.target.checked)}
              className="accent-accent"
            />
            {intl.formatMessage({ id: "tobis.filter.hasScriptLabel", defaultMessage: "Available" })}
          </label>
        </Field>

        {/* Standardmaessig an (EMPTY_TITLE_FILTERS.hasImage) - eine Kachelliste
            mit vielen AbstractTile-Platzhaltern wirkt durchmischt und unruhig,
            kein Datenkriterium wie hasScript oben. */}
        <Field label={intl.formatMessage({ id: "tobis.filter.hasImage", defaultMessage: "Cover" })}>
          <label className="flex h-9 cursor-pointer items-center gap-2 text-[13px] text-ink/80">
            <input
              type="checkbox"
              checked={filters.hasImage}
              onChange={(e) => set("hasImage", e.target.checked)}
              className="accent-accent"
            />
            {intl.formatMessage({ id: "tobis.filter.hasImageLabel", defaultMessage: "Only with cover" })}
          </label>
        </Field>

        {/* Daten-Deckungs-/Qualitaetsfilter (bulk.lumiere_film_identifier),
            kein fachliches Kriterium wie die Felder oben - feste Schluessel
            statt FilterOptions-Werten, deshalb hier und nicht per asOptions. */}
        <Field label={intl.formatMessage({ id: "tobis.filter.identifiers", defaultMessage: "Identifiers" })}>
          <MultiSelect
            options={[
              { value: "imdb", label: intl.formatMessage({ id: "tobis.filter.identifierImdb", defaultMessage: "IMDb ID" }) },
              { value: "lumiere", label: intl.formatMessage({ id: "tobis.filter.identifierLumiere", defaultMessage: "LUMIERE ID" }) },
              { value: "isan", label: intl.formatMessage({ id: "tobis.filter.identifierIsan", defaultMessage: "ISAN" }) },
              { value: "wikidata", label: intl.formatMessage({ id: "tobis.filter.identifierWikidata", defaultMessage: "Wikidata" }) },
              { value: "eidr", label: intl.formatMessage({ id: "tobis.filter.identifierEidr", defaultMessage: "EIDR" }) },
            ]}
            selected={filters.identifiers}
            onChange={(next) => set("identifiers", next)}
            placeholder={intl.formatMessage({ id: "tobis.filter.identifiers", defaultMessage: "Identifiers" })}
            className="w-full"
          />
        </Field>
      </div>
    </div>
  );
}

export function TobisTalentFilterBar({
  filters,
  onChange,
  options,
  sort,
  onSortChange,
}: {
  filters: TobisTalentFilters;
  onChange: (next: TobisTalentFilters) => void;
  options: FilterOptions | null;
  sort: TobisTalentSort;
  onSortChange: (next: TobisTalentSort) => void;
}) {
  const intl = useIntl();
  const set = <K extends keyof TobisTalentFilters>(key: K, value: TobisTalentFilters[K]) =>
    onChange({ ...filters, [key]: value });

  // Kein eigenes Label daneben: die gewaehlte Option ("Meiste Rollen"/"Name
  // A–Z") ist selbsterklaerend, genau wie beim Sort-Select in
  // SaveCollectionTemplate.tsx (dasselbe Select-Primitive, gleiche Pill-Optik).
  const sortOptions: FieldOption[] = [
    { value: "credits", label: intl.formatMessage({ id: "tobis.sort.roles", defaultMessage: "Most roles" }) },
    { value: "az", label: intl.formatMessage({ id: "sort.nameAz", defaultMessage: "Name A–Z" }) },
    {
      value: "admissions",
      label: intl.formatMessage({ id: "tobis.sort.admissions", defaultMessage: "Most admissions" }),
    },
    {
      value: "updated",
      label: intl.formatMessage({ id: "tobis.sort.updated", defaultMessage: "Recently updated" }),
    },
  ];

  // Dieselben drei Werte/Labels wie der Medium-Filter der Titelliste (siehe
  // TobisTitleFilterBar) - "mindestens ein Credit dieses Mediums", nicht "ist
  // dieses Medium" wie bei einem Titel selbst, siehe TalentFilters.mediumType.
  const mediaTypes: Array<{ value: TobisTalentFilters["mediumType"]; labelId: string; fallback: string }> = [
    { value: "", labelId: "tobis.filter.all", fallback: "All" },
    { value: "movie", labelId: "tobis.filter.movies", fallback: "Films" },
    { value: "series", labelId: "tobis.filter.series", fallback: "Series" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <TobisSearch
          value={filters.search}
          onChange={(v) => set("search", v)}
          label={intl.formatMessage({
            id: "tobis.people.searchLabel",
            defaultMessage: "Search people",
          })}
          placeholder={intl.formatMessage({
            id: "tobis.people.searchPlaceholder",
            defaultMessage: "Search by name",
          })}
        />
        <div className="flex gap-1.5">
          {mediaTypes.map((m) => (
            <FilterChip
              key={m.value || "all"}
              active={filters.mediumType === m.value}
              onClick={() => set("mediumType", m.value)}
            >
              {intl.formatMessage({ id: m.labelId, defaultMessage: m.fallback })}
            </FilterChip>
          ))}
        </div>
        {talentFiltersActive(filters) ? (
          <ResetButton onClick={() => onChange(EMPTY_TALENT_FILTERS)} />
        ) : null}
        <div className="ml-auto">
          <Select
            value={sort}
            onChange={(v) => onSortChange(v as TobisTalentSort)}
            options={sortOptions}
            hideEmpty
            className="w-40 !rounded-full !bg-page !py-1.5 !text-[12px]"
          />
        </div>
      </div>

      {/* SHARE-FILTER-Zuschnitt (ExtendedSearchPage): Label oben, Control
          darunter, alle Felder in einem Grid statt einzeiliger Reihe.
          grid-cols-N statt [repeat(N,max-content)], w-full statt fester
          Breite - siehe der ausfuehrliche Kommentar bei TobisTitleFilterBar. */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 @sm:grid-cols-4">
        <Field label={intl.formatMessage({ id: "tobis.filter.countries", defaultMessage: "Countries" })}>
          <MultiSelect
            options={asOptions(options?.countries ?? [])}
            selected={filters.countries}
            onChange={(next) => set("countries", next)}
            placeholder={intl.formatMessage({
              id: "tobis.filter.countries",
              defaultMessage: "Countries",
            })}
            searchable
            className="w-full"
          />
        </Field>

        <Field label={intl.formatMessage({ id: "tobis.filter.age", defaultMessage: "Age" })}>
          <div className="flex items-center gap-2">
            <RangeInput
              value={filters.ageFrom}
              onChange={(v) => set("ageFrom", v)}
              label={intl.formatMessage({ id: "tobis.filter.ageFrom", defaultMessage: "Age from" })}
              placeholder="30"
              width="w-14"
            />
            <span className="text-[12px] text-ink/30">–</span>
            <RangeInput
              value={filters.ageTo}
              onChange={(v) => set("ageTo", v)}
              label={intl.formatMessage({ id: "tobis.filter.ageTo", defaultMessage: "Age to" })}
              placeholder="60"
              width="w-14"
            />
          </div>
        </Field>

        <Field label={intl.formatMessage({ id: "tobis.filter.rolesFrom", defaultMessage: "Roles from" })}>
          <RangeInput
            value={filters.creditsMin}
            onChange={(v) => set("creditsMin", v)}
            label={intl.formatMessage({
              id: "tobis.filter.rolesFrom",
              defaultMessage: "Roles from",
            })}
            placeholder="2"
            width="w-14"
          />
        </Field>

        {/* Land/Alter wirken faktisch nur auf TVmaze-Personen (0,12% des
            Bestands): TMDB liefert dafuer kein country/birthday. Der
            Quelle-Filter macht das steuerbar statt es zu verstecken. */}
        <Field label={intl.formatMessage({ id: "tobis.filter.source", defaultMessage: "Source" })}>
          <SegmentedControl
            value={filters.source}
            onChange={(v) => set("source", v)}
            size="sm"
            options={[
              { value: "", label: intl.formatMessage({ id: "tobis.filter.all", defaultMessage: "All" }) },
              {
                value: "tvmaze",
                label: intl.formatMessage({ id: "tobis.filter.sourceTvmaze", defaultMessage: "TVmaze" }),
              },
              {
                value: "tmdb",
                label: intl.formatMessage({ id: "tobis.filter.sourceTmdb", defaultMessage: "TMDB" }),
              },
            ]}
          />
        </Field>

        {/* Aus model.credit abgeleitet, kein Feld auf Talent: "Crew" ist
            absichtlich der breiteste Eimer (jeder nicht-schauspielerische
            Job), Regie/Produktion greifen zusaetzlich enger fuer die
            wichtigsten Crew-Rollen. Siehe ROLE_TYPE_CONDITION im BFF. */}
        <Field label={intl.formatMessage({ id: "tobis.filter.roleType", defaultMessage: "Type" })}>
          <SegmentedControl
            value={filters.roleType}
            onChange={(v) => set("roleType", v)}
            size="sm"
            options={[
              { value: "", label: intl.formatMessage({ id: "tobis.filter.all", defaultMessage: "All" }) },
              {
                value: "acting",
                label: intl.formatMessage({ id: "tobis.filter.roleTypeActing", defaultMessage: "Actors" }),
              },
              {
                value: "directing",
                label: intl.formatMessage({ id: "tobis.filter.roleTypeDirecting", defaultMessage: "Directing" }),
              },
              {
                value: "producing",
                label: intl.formatMessage({ id: "tobis.filter.roleTypeProducing", defaultMessage: "Producing" }),
              },
              {
                value: "crew",
                label: intl.formatMessage({ id: "tobis.filter.roleTypeCrew", defaultMessage: "Crew" }),
              },
            ]}
          />
        </Field>
      </div>
    </div>
  );
}
